// src/features/explore/DiscoveryRow.tsx — Trending Topics / Suggested People / Featured Communities.
import styled from 'styled-components';
import { media } from '@/theme/media';
import { ConsolePanel, Avatar, Button } from '@/ui';
import {
  useTrends,
  useSuggestedProfiles,
  useCommunities,
  useFollow,
  useRequireAuth,
  followButtonLabel,
} from '@/hooks';
import { PanelEmpty } from '@/components/panels/PanelEmpty';
import type { SuggestedProfile } from '@/core/models/account';
import { DiscoveryCommunityCard } from './DiscoveryCommunityCard';
import { FeaturedCommunity } from './FeaturedCommunity';

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  margin-bottom: 18px;
  ${media.tabletDown`grid-template-columns: 1fr;`}
`;
const TopicList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(105px, 1fr));
  gap: 10px;
  button {
    padding: 12px;
    min-height: 76px;
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    border-radius: ${({ theme }) => theme.radius.md};
  }
`;
const CommunityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
  gap: 12px;
`;
const Heading = styled.h2`
  margin: 0 0 10px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const ItemRow = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;
const Name = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Sub = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const TrendButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.radius.sm};
  }
`;
const Tag = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

function FollowRow({ profile }: { profile: SuggestedProfile }) {
  const { isFollowing, isRequested, toggleFollow } = useFollow(profile.handle);
  const requireAuth = useRequireAuth();
  function handleFollow() {
    if (!requireAuth('follow')) return;
    void toggleFollow();
  }
  return (
    <ItemRow>
      <Avatar name={profile.name} size={36} />
      <Info>
        <Name>{profile.name}</Name>
        <Sub>{profile.handle}</Sub>
      </Info>
      <Button
        variant={isFollowing || isRequested ? 'secondary' : 'outline'}
        onClick={handleFollow}
        aria-label={`${followButtonLabel(isFollowing, isRequested)} ${profile.handle}`}
      >
        {followButtonLabel(isFollowing, isRequested)}
      </Button>
    </ItemRow>
  );
}

export interface DiscoveryRowProps {
  /** Called when a trending topic is clicked (tag without the leading "#"). */
  onSelectTrend: (tag: string) => void;
}

export function DiscoveryRow({ onSelectTrend }: DiscoveryRowProps) {
  const { trends } = useTrends();
  const profiles = useSuggestedProfiles();
  const { communities } = useCommunities();

  return (
    <Row>
      <FeaturedCommunity />
      <ConsolePanel>
        <Heading>Trending Topics</Heading>
        {trends.length === 0 ? (
          <PanelEmpty>Nothing trending yet.</PanelEmpty>
        ) : (
          <TopicList>
            {trends.slice(0, 5).map((t) => (
              <div key={t.tag}>
                <TrendButton
                  type="button"
                  aria-label={t.tag}
                  onClick={() => onSelectTrend(t.tag.replace(/^#/, ''))}
                >
                  <Tag>{t.tag}</Tag>
                  <Sub>{t.count}</Sub>
                </TrendButton>
              </div>
            ))}
          </TopicList>
        )}
      </ConsolePanel>

      <ConsolePanel>
        <Heading>Featured Communities</Heading>
        {communities.length === 0 ? (
          <PanelEmpty>No communities yet.</PanelEmpty>
        ) : (
          <CommunityGrid>
            {communities.slice(0, 4).map((c) => (
              <DiscoveryCommunityCard key={c.id} community={c} />
            ))}
          </CommunityGrid>
        )}
      </ConsolePanel>
      <ConsolePanel>
        <Heading>Suggested People</Heading>
        {profiles.length === 0 ? (
          <PanelEmpty>No suggestions yet.</PanelEmpty>
        ) : (
          <List>
            {profiles.slice(0, 3).map((p) => (
              <FollowRow key={p.handle} profile={p} />
            ))}
          </List>
        )}
      </ConsolePanel>
    </Row>
  );
}
